import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Alert, Col, Form, Nav, Row, Spinner, Tab } from "react-bootstrap";
import { groupBy, isEmpty } from "lodash";
import { IProduct } from "./Product";
import { Position } from "./Pagination";
import Papa from "papaparse";
import ProductGrid from "./ProductGrid";

interface Props {
  hidePrices?: boolean
}

// Initialize store from URL parameters.
const store = new URLSearchParams(window.location.search).get("store");
const stores = {
  "driesprong": "https://warentuin.nl/frameshop/feed/driesprong.csv",
  "tuinextra": "https://warentuin.nl/frameshop/feed/tuinextra.csv"
} as { [key: string]: string };

const Shop = ({ hidePrices }: Props) => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Initialize products.
  useEffect(() => {
    if (!store || !Object.keys(stores).includes(store)) return;
    Papa.parse(stores[store], {
      complete: result => {
        setProducts(result.data as IProduct[]);
      },
      download: true,
      transform: (value: string, field: string | number) => {
        if ("prijs" === field || "vanprijs" === field) {
          return parseFloat(value.replace(" EUR", ""));
        }
        return value;
      },
      error: error => {
        console.error(error.message);
      },
      fastMode: true,
      header: true,
      skipEmptyLines: true,
    });
  }, []);
  
  // Group products by category.
  const productsByCategory = useMemo(() => {
    return groupBy(products, "category");
  }, [products]);
  
  // Get a list of unique product categories.
  const categoryNames = Object.keys(productsByCategory);

  // Determine new search results after products or search term has changed.
  const productSearchResults = useMemo(() => {
    if (!searchTerm.length) return products;
    return products
      .filter(product => 
        product.name.includes(searchTerm) ||
        product.category.includes(searchTerm) ||
        product.sku.includes(searchTerm)
      )
  }, [products, searchTerm]);

  // Show a loading spinner on initialization.
  if (isEmpty(products)) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="sr-only">Webshop is aan het laden...</span>
        </Spinner>
      </div>
    )
  }

  // Show the shop.
  return (
    <>
      <Alert variant="info">
        <FontAwesomeIcon icon={faInfoCircle} className="mr-2"/>
        <span>De getoonde prijzen kunnen afwijken van de prijzen in de winkel.</span>
      </Alert>
      <Tab.Container defaultActiveKey="category-1">
        <Nav variant="pills">
          <Nav.Item>
            <Nav.Link eventKey="search">
              <FontAwesomeIcon icon={faSearch}/>
            </Nav.Link>
          </Nav.Item>
          {
            categoryNames.map((categoryName, i) => {
              return(
                <Nav.Item key={i}>
                  <Nav.Link eventKey={`category-${i+1}`}>{categoryName}</Nav.Link>
                </Nav.Item>
              )
            })
          }
        </Nav>
        <Tab.Content className="mt-4">
          <Tab.Pane eventKey="search" mountOnEnter>
            <Row>
              <Col>
                <Form>
                  <Form.Group controlId="search">
                    <Form.Control
                      autoFocus
                      onChange={(event) => setSearchTerm(event.currentTarget.value)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => { if (13 === event.keyCode) event.preventDefault(); }}
                      placeholder="Vind een product..."
                      size="lg"
                      type="search" />
                  </Form.Group>
                </Form>
              </Col>
            </Row>
            <ProductGrid
              pagination={Position.Bottom}
              products={productSearchResults} />
          </Tab.Pane>
          {
            categoryNames.map((categoryName, i) =>
              <Tab.Pane key={i} eventKey={`category-${i+1}`} mountOnEnter>
                <ProductGrid
                  hidePrices={hidePrices}
                  pagination={Position.Both}
                  products={productsByCategory[categoryName]}
                  sort />
              </Tab.Pane>
            )
          }
        </Tab.Content>
      </Tab.Container>
    </>
  );
}

export default Shop;
